import * as React from 'react';
import { importHooks } from '../network/endpoints.js';
import { Money } from '../../../components/ui/Money.js';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/FormField';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { useToast } from '../../../components/ui/Toast';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import { Decimal } from 'decimal.js';
import type {
  ImportDetail,
  ReconciliationCorrectionPayload,
  ReconciliationRow,
  ReconciliationRowStatus,
} from '../types.js';

/** The API rejects a reason shorter than this, so the button mirrors it instead of failing late. */
const MIN_REASON_LENGTH = 10;

function StatusCell({ status }: { status: ReconciliationRowStatus }) {
  if (status === 'MATCHED') return <StatusBadge status="success" label="Matched" />;
  if (status === 'IGNORED') return <StatusBadge status="info" label="Resolved" />;
  return <StatusBadge status="warning" label="Awaiting match" />;
}

function Counter({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`p-4 rounded text-center ${tone}`}>
      <p className="text-sm">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

export function ReconciliationResults({ importId }: { importId: string }) {
  const { data, isLoading } = importHooks.useGet(importId);
  const matchMutation = importHooks.useMatch();
  const resolveMutation = importHooks.useResolveRow();
  const correctMutation = importHooks.useCorrectRow();
  const { toast } = useToast();

  const [resolveTarget, setResolveTarget] = React.useState<ReconciliationRow | null>(null);
  const [resolveReason, setResolveReason] = React.useState('');
  const [correctTarget, setCorrectTarget] = React.useState<ReconciliationRow | null>(null);
  const [correctReason, setCorrectReason] = React.useState('');
  const [correctForm, setCorrectForm] = React.useState({
    rowDate: '',
    description: '',
    amount: '',
    bankRef: '',
  });

  if (isLoading) {
    return <div className="p-6">Loading import details...</div>;
  }

  // `useGet` returns the API's `{ data: <detail> }` envelope already unwrapped (the fetcher
  // strips it), so the detail is `data` itself. The explicit annotation is what pins the shape:
  // the resource hook is typed `any`, and the Zod schema on the client is only applied when the
  // body parses.
  const importDetail: ImportDetail | undefined = data;

  if (!importDetail) {
    return <div className="p-6">Import not found</div>;
  }

  const rows = importDetail.rows;
  const awaitingRows = rows.filter((row) => row.status === 'UNMATCHED');
  const matchedRows = rows.filter((row) => row.status === 'MATCHED');
  const resolvedRows = rows.filter((row) => row.status === 'IGNORED');

  const handleManualMatch = (rowId: string) => {
    const instructionId = prompt('Enter Instruction ID to match with:');
    if (instructionId) {
      matchMutation.mutate({ importId, instructionId, rowId });
    }
  };

  const openResolve = (row: ReconciliationRow) => {
    setResolveReason('');
    setResolveTarget(row);
  };

  const openCorrect = (row: ReconciliationRow) => {
    setCorrectReason(row.correctionNote ?? '');
    setCorrectForm({
      rowDate: row.rowDate,
      description: row.description,
      // amount is stored as integer paise; the form edits rupees, so it converts on both ends.
      amount: new Decimal(row.amount).div(100).toFixed(2),
      bankRef: row.bankRef ?? '',
    });
    setCorrectTarget(row);
  };

  const submitResolve = () => {
    if (!resolveTarget) return;
    resolveMutation.mutate(
      { importId, rowId: resolveTarget.id, reason: resolveReason.trim() },
      {
        onSuccess: () => {
          setResolveTarget(null);
          toast({
            type: 'success',
            title: 'Row resolved',
            description: 'The row has been closed and left the unmatched queue.',
          });
        },
        onError: (error) => {
          toast({
            type: 'error',
            title: 'Resolve failed',
            description: error instanceof Error ? error.message : 'Could not resolve the row',
          });
        },
      },
    );
  };

  const submitCorrect = () => {
    if (!correctTarget) return;

    const correction: ReconciliationCorrectionPayload = { reason: correctReason.trim() };
    const description = correctForm.description.trim();
    const rowDate = correctForm.rowDate.trim();
    const bankRef = correctForm.bankRef.trim();
    if (description !== '') correction.description = description;
    if (rowDate !== '') correction.rowDate = rowDate;
    if (bankRef !== '') correction.bankRef = bankRef;

    const amountText = correctForm.amount.trim();
    if (amountText !== '') {
      // Decimal only throws on input it cannot parse, so screen the shape first instead of
      // catching a parse failure: a malformed amount is an operator typo, not an exception.
      if (!/^-?\d+(?:\.\d+)?$/.test(amountText)) {
        toast({
          type: 'error',
          title: 'Invalid amount',
          description: 'Amount must be a number in rupees.',
        });
        return;
      }
      const paise = new Decimal(amountText).times(100);
      if (!paise.isInteger()) {
        toast({
          type: 'error',
          title: 'Invalid amount',
          description: 'Amount must be in rupees with at most 2 decimal places.',
        });
        return;
      }
      correction.amount = paise.toNumber();
    }

    correctMutation.mutate(
      { importId, rowId: correctTarget.id, correction },
      {
        onSuccess: () => {
          setCorrectTarget(null);
          toast({
            type: 'success',
            title: 'Row corrected',
            description:
              'The imported value has been corrected and the row is back in the unmatched queue.',
          });
        },
        onError: (error) => {
          toast({
            type: 'error',
            title: 'Correction failed',
            description: error instanceof Error ? error.message : 'Could not correct the row',
          });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-surface-1 p-6 rounded-lg shadow-sm border border-border">
        <h2 className="text-xl font-bold mb-4">Import Details: {importDetail.fileRef}</h2>
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-surface-2 p-4 rounded text-center">
            <p className="text-sm text-text-secondary">Status</p>
            <p className="text-lg font-semibold">{importDetail.status}</p>
          </div>
          <Counter
            label="Matched"
            value={importDetail.matchedRows}
            tone="bg-success-bg text-success-text"
          />
          <Counter
            label="Awaiting match"
            value={importDetail.unmatchedRows}
            tone="bg-warning-bg text-warning-text"
          />
          <Counter
            label="Resolved"
            value={importDetail.ignoredRows ?? 0}
            tone="bg-info-bg text-info-text"
          />
        </div>

        <h3 className="text-lg font-semibold mb-3">Awaiting Match</h3>
        <p className="text-sm text-text-secondary mb-3">
          Match an instruction when the bank row belongs to one, correct the value if the statement
          was imported wrongly, or resolve the row with a reason when nothing will ever match it.
        </p>
        <div className="overflow-x-auto mb-8 border border-border rounded">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">UTR</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface-1">
              {awaitingRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-text-muted">
                    No unmatched rows
                  </td>
                </tr>
              ) : (
                awaitingRows.map((row) => (
                  <tr key={row.id} data-testid="awaiting-row">
                    <td className="px-6 py-4 text-sm">{row.rowDate}</td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate" title={row.description}>
                      {row.description}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Money
                        paise={row.amount}
                        currency={DEFAULT_CURRENCY}
                        locale={DEFAULT_LOCALE}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm">{row.bankRef ?? '—'}</td>
                    <td className="px-6 py-4 text-sm">
                      <StatusCell status={row.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleManualMatch(row.id)}
                        disabled={matchMutation.isPending}
                      >
                        Match
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openCorrect(row)}
                        disabled={correctMutation.isPending}
                      >
                        Correct
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openResolve(row)}
                        disabled={resolveMutation.isPending}
                      >
                        Resolve
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold mb-3">Resolved</h3>
        <p className="text-sm text-text-secondary mb-3">
          Rows closed with a reason. Correcting a resolved row puts it back in the unmatched queue.
        </p>
        <div className="overflow-x-auto mb-8 border border-border rounded">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Resolution Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Resolved</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface-1">
              {resolvedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-text-muted">
                    No resolved rows
                  </td>
                </tr>
              ) : (
                resolvedRows.map((row) => (
                  <tr key={row.id} data-testid="resolved-row">
                    <td className="px-6 py-4 text-sm">{row.rowDate}</td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate" title={row.description}>
                      {row.description}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Money
                        paise={row.amount}
                        currency={DEFAULT_CURRENCY}
                        locale={DEFAULT_LOCALE}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm" title={row.resolutionNote ?? undefined}>
                      {row.resolutionNote ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-text-secondary">
                      {row.resolvedAt ? new Date(row.resolvedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openCorrect(row)}
                        disabled={correctMutation.isPending}
                      >
                        Correct
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold mb-3">Matched</h3>
        <div className="overflow-x-auto border border-border rounded">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">Instruction ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface-1">
              {matchedRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-text-muted">
                    No matched rows
                  </td>
                </tr>
              ) : (
                matchedRows.map((row) => (
                  <tr key={row.id} data-testid="matched-row">
                    <td className="px-6 py-4 text-sm">{row.rowDate}</td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate" title={row.description}>
                      {row.description}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <Money
                        paise={row.amount}
                        currency={DEFAULT_CURRENCY}
                        locale={DEFAULT_LOCALE}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-text-secondary">{row.instructionId ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={resolveTarget !== null} onOpenChange={(open) => !open && setResolveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Row</DialogTitle>
            <DialogDescription>
              Closes this statement row without a match. The reason is stored on the row and in the
              audit log.
            </DialogDescription>
          </DialogHeader>
          {resolveTarget && (
            <div className="flex flex-col gap-4 py-4">
              <div className="bg-surface-2 border border-border rounded p-3 text-sm">
                <p className="text-text-primary">{resolveTarget.description}</p>
                <p className="text-text-secondary mt-1">
                  {resolveTarget.rowDate} ·{' '}
                  <Money
                    paise={resolveTarget.amount}
                    currency={DEFAULT_CURRENCY}
                    locale={DEFAULT_LOCALE}
                  />
                </p>
              </div>
              <FormField
                label={`Reason (min ${MIN_REASON_LENGTH} characters)`}
                htmlFor="resolveReason"
                required
              >
                <Input
                  id="resolveReason"
                  value={resolveReason}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setResolveReason(e.target.value)
                  }
                  placeholder="Why will this row never match an instruction?"
                />
              </FormField>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setResolveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitResolve}
              loading={resolveMutation.isPending}
              disabled={resolveReason.trim().length < MIN_REASON_LENGTH || resolveMutation.isPending}
            >
              Resolve Row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={correctTarget !== null} onOpenChange={(open) => !open && setCorrectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct Row</DialogTitle>
            <DialogDescription>
              Fixes the imported value directly. A resolved row is reopened into the unmatched
              queue; matched rows cannot be corrected.
            </DialogDescription>
          </DialogHeader>
          {correctTarget && (
            <div className="flex flex-col gap-4 py-4">
              <FormField label="Row date" htmlFor="correctRowDate">
                <Input
                  id="correctRowDate"
                  type="date"
                  value={correctForm.rowDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCorrectForm((prev) => ({ ...prev, rowDate: e.target.value }))
                  }
                />
              </FormField>
              <FormField label="Description" htmlFor="correctDescription">
                <Input
                  id="correctDescription"
                  value={correctForm.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCorrectForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </FormField>
              <FormField
                label="Amount (rupees)"
                htmlFor="correctAmount"
                hint="Leave as-is unless the statement value itself was wrong."
              >
                <Input
                  id="correctAmount"
                  inputMode="decimal"
                  value={correctForm.amount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCorrectForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                />
              </FormField>
              <FormField label="Bank reference (UTR)" htmlFor="correctBankRef">
                <Input
                  id="correctBankRef"
                  value={correctForm.bankRef}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCorrectForm((prev) => ({ ...prev, bankRef: e.target.value }))
                  }
                />
              </FormField>
              <FormField
                label={`Reason (min ${MIN_REASON_LENGTH} characters)`}
                htmlFor="correctReason"
                required
              >
                <Input
                  id="correctReason"
                  value={correctReason}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCorrectReason(e.target.value)
                  }
                  placeholder="Why is this value wrong?"
                />
              </FormField>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCorrectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitCorrect}
              loading={correctMutation.isPending}
              disabled={correctReason.trim().length < MIN_REASON_LENGTH || correctMutation.isPending}
            >
              Save Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
